import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Plus, Trash, Shield, User as UserIcon, Sword, Heart, Sparkle, Users } from '@phosphor-icons/react';
import BottomNav from '../components/BottomNav';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = () => axios.create({ baseURL: API, withCredentials: true });

const STAT_LABELS = { strength: 'STR', dexterity: 'DEX', constitution: 'CON', intelligence: 'INT', wisdom: 'WIS', charisma: 'CHA' };
const EMPTY_SHEET = {
  name: '',
  class_name: '',
  race: '',
  level: 1,
  hp_current: 10,
  hp_max: 10,
  armor_class: 10,
  ability_scores: { strength: 10, dexterity: 10, constitution: 10, intelligence: 10, wisdom: 10, charisma: 10 },
  proficient_skills: [],
  inventory: [],
  background: '',
  alignment: '',
  notes: '',
  is_shared: false,
};

export default function CharacterSheetsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('mine'); // 'mine' | 'shared'
  const [sheets, setSheets] = useState([]);
  const [ref, setRef] = useState({ classes: [], races: [], skills: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // sheet object or 'new'

  const load = async () => {
    setLoading(true);
    try {
      const [r, list] = await Promise.all([
        ax().get('/api/character-sheets/reference'),
        ax().get(tab === 'mine' ? '/api/character-sheets' : '/api/character-sheets/shared'),
      ]);
      setRef(r.data);
      setSheets(Array.isArray(list.data) ? list.data : []);
    } catch (e) {
      toast.error('Failed to load character sheets');
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  return (
    <div className="app-container" data-testid="character-sheets-page">
      <header className="p-6 pb-3">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-[#181824] flex items-center justify-center" data-testid="back-button">
            <ArrowLeft size={20} weight="bold" className="text-white" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              <Shield size={24} weight="duotone" className="text-[#F59E0B]" />
              D&D Character Sheets
            </h1>
            <p className="text-[#71717A] text-xs">Simplified 5e sheets — private by default</p>
          </div>
        </div>

        <div className="flex gap-2 mb-3">
          <button onClick={() => setTab('mine')} className={`flex-1 py-2 rounded-xl text-sm font-medium transition-all ${tab === 'mine' ? 'bg-[#F59E0B] text-black' : 'bg-[#181824] text-[#A1A1AA] border border-white/5'}`} data-testid="tab-mine">
            <UserIcon size={14} weight="bold" className="inline mr-1" /> Mine
          </button>
          <button onClick={() => setTab('shared')} className={`flex-1 py-2 rounded-xl text-sm font-medium transition-all ${tab === 'shared' ? 'bg-[#F59E0B] text-black' : 'bg-[#181824] text-[#A1A1AA] border border-white/5'}`} data-testid="tab-shared">
            <Users size={14} weight="bold" className="inline mr-1" /> Shared with community
          </button>
        </div>

        {tab === 'mine' && (
          <button onClick={() => setEditing('new')} className="w-full py-3 rounded-xl bg-gradient-to-r from-[#F59E0B] to-[#EA580C] text-black font-semibold text-sm flex items-center justify-center gap-2" data-testid="new-sheet-btn">
            <Plus size={16} weight="bold" /> New Character Sheet
          </button>
        )}
      </header>

      <div className="px-6 pb-36 space-y-3">
        {loading ? (
          [1, 2].map(i => <div key={i} className="h-24 bg-white/[0.03] rounded-xl animate-pulse" />)
        ) : sheets.length === 0 ? (
          <div className="text-center py-12">
            <Shield size={40} weight="duotone" className="text-[#333] mx-auto mb-2" />
            <p className="text-[#71717A] text-sm">
              {tab === 'mine' ? 'No character sheets yet — create your first!' : 'No shared sheets yet'}
            </p>
          </div>
        ) : (
          sheets.map(s => (
            <SheetCard key={s.id} sheet={s} onClick={() => setEditing(s)} isMine={tab === 'mine'} />
          ))
        )}
      </div>

      <AnimatePresence>
        {editing && (
          <EditSheetModal
            initial={editing === 'new' ? EMPTY_SHEET : editing}
            isNew={editing === 'new'}
            ref_={ref}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }}
          />
        )}
      </AnimatePresence>

      <BottomNav />
    </div>
  );
}

function SheetCard({ sheet, onClick, isMine }) {
  return (
    <button onClick={onClick} className="w-full text-left bg-[#0e0e16] border border-white/[0.06] rounded-xl p-4 hover:border-[#F59E0B]/30 transition-all" data-testid={`sheet-${sheet.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <p className="text-white font-semibold text-base">{sheet.name}</p>
          <p className="text-[#A1A1AA] text-xs">
            Level {sheet.level} {sheet.race} {sheet.class_name}
          </p>
          {!isMine && sheet.owner_name && <p className="text-[#666] text-[10px] mt-0.5">by {sheet.owner_name}</p>}
        </div>
        {sheet.is_shared && (
          <span className="text-[9px] px-2 py-0.5 rounded-full bg-[#39FF14]/10 text-[#39FF14] border border-[#39FF14]/30 uppercase tracking-wider">Shared</span>
        )}
      </div>
      <div className="flex items-center gap-3 text-xs">
        <span className="flex items-center gap-1 text-[#EF4444]"><Heart size={12} weight="fill" /> {sheet.hp_current}/{sheet.hp_max}</span>
        <span className="flex items-center gap-1 text-[#3B82F6]"><Shield size={12} weight="fill" /> AC {sheet.armor_class}</span>
        <span className="flex items-center gap-1 text-[#A1A1AA]"><Sword size={12} /> {(sheet.inventory || []).length} items</span>
      </div>
    </button>
  );
}

function EditSheetModal({ initial, isNew, ref_, onClose, onSaved }) {
  const [form, setForm] = useState({ ...initial, ability_scores: { ...initial.ability_scores } });
  const [saving, setSaving] = useState(false);
  const [newItem, setNewItem] = useState('');

  const save = async () => {
    if (!form.name.trim() || !form.class_name || !form.race) {
      toast.error('Name, class, and race are required');
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await ax().post('/api/character-sheets', form);
        toast.success('Character sheet created');
      } else {
        await ax().patch(`/api/character-sheets/${form.id}`, form);
        toast.success('Character sheet saved');
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${form.name}"? This can't be undone.`)) return;
    try {
      await ax().delete(`/api/character-sheets/${form.id}`);
      toast.success('Deleted');
      onSaved();
    } catch (e) { toast.error('Delete failed'); }
  };

  const toggleSkill = (skill) => {
    setForm(f => ({
      ...f,
      proficient_skills: f.proficient_skills.includes(skill)
        ? f.proficient_skills.filter(s => s !== skill)
        : [...f.proficient_skills, skill],
    }));
  };

  const addItem = () => {
    const t = newItem.trim();
    if (!t) return;
    setForm(f => ({ ...f, inventory: [...(f.inventory || []), { name: t, qty: 1 }] }));
    setNewItem('');
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 100 }} animate={{ y: 0 }} exit={{ y: 100 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-[480px] bg-[#0e0e16] border-t border-white/10 rounded-t-2xl p-5 max-h-[90vh] overflow-y-auto"
        data-testid="sheet-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-bold text-base flex items-center gap-2">
            <Shield size={20} weight="duotone" className="text-[#F59E0B]" />
            {isNew ? 'New Sheet' : form.name || 'Edit Sheet'}
          </h3>
          <button onClick={onClose} className="text-[#555] hover:text-white text-sm">Cancel</button>
        </div>

        <div className="space-y-3">
          <input className="input-field" placeholder="Character name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="input-name" />

          <div className="grid grid-cols-2 gap-2">
            <select className="input-field" value={form.class_name} onChange={e => setForm({ ...form, class_name: e.target.value })} data-testid="select-class">
              <option value="">Class…</option>
              {ref_.classes.map(c => <option key={c}>{c}</option>)}
            </select>
            <select className="input-field" value={form.race} onChange={e => setForm({ ...form, race: e.target.value })} data-testid="select-race">
              <option value="">Race…</option>
              {ref_.races.map(r => <option key={r}>{r}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <NumField label="Level" value={form.level} onChange={v => setForm({ ...form, level: v })} min={1} max={20} testid="input-level" />
            <NumField label="HP cur" value={form.hp_current} onChange={v => setForm({ ...form, hp_current: v })} min={0} testid="input-hp-cur" />
            <NumField label="HP max" value={form.hp_max} onChange={v => setForm({ ...form, hp_max: v })} min={1} testid="input-hp-max" />
          </div>
          <NumField label="Armor Class" value={form.armor_class} onChange={v => setForm({ ...form, armor_class: v })} min={0} max={40} testid="input-ac" />

          <div className="pt-2">
            <p className="text-[#A1A1AA] text-xs mb-2 uppercase tracking-wider">Ability Scores</p>
            <div className="grid grid-cols-3 gap-2">
              {Object.keys(STAT_LABELS).map(k => (
                <div key={k} className="flex items-center gap-1">
                  <span className="text-[#F59E0B] text-xs font-semibold w-8">{STAT_LABELS[k]}</span>
                  <input type="number" min="1" max="30" className="input-field flex-1 px-2 py-1 text-sm text-center"
                    value={form.ability_scores[k]}
                    onChange={e => setForm({ ...form, ability_scores: { ...form.ability_scores, [k]: Number(e.target.value || 0) } })}
                    data-testid={`stat-${k}`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="pt-2">
            <p className="text-[#A1A1AA] text-xs mb-2 uppercase tracking-wider">Proficient Skills</p>
            <div className="flex flex-wrap gap-1.5">
              {ref_.skills.map(s => (
                <button key={s} type="button" onClick={() => toggleSkill(s)}
                  className={`px-2.5 py-1 rounded-full text-[11px] transition-all ${form.proficient_skills.includes(s) ? 'bg-[#F59E0B] text-black' : 'bg-[#181824] text-[#A1A1AA] border border-white/5'}`}
                  data-testid={`skill-${s.replace(/\s+/g, '-').toLowerCase()}`}
                >{s}</button>
              ))}
            </div>
          </div>

          <div className="pt-2">
            <p className="text-[#A1A1AA] text-xs mb-2 uppercase tracking-wider">Inventory</p>
            <div className="space-y-1 mb-2">
              {(form.inventory || []).map((it, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-[#181824] border border-white/5">
                  <span className="text-white text-sm flex-1">{it.name}</span>
                  <span className="text-[#71717A] text-xs">×{it.qty}</span>
                  <button onClick={() => setForm(f => ({ ...f, inventory: f.inventory.filter((_, ix) => ix !== i) }))} className="text-[#555] hover:text-[#EF4444]">
                    <Trash size={14} />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input className="input-field flex-1" placeholder="Add item…" value={newItem} onChange={e => setNewItem(e.target.value)} onKeyDown={e => e.key === 'Enter' && addItem()} data-testid="input-new-item" />
              <button onClick={addItem} className="px-3 py-2 rounded-xl bg-[#F59E0B] text-black text-sm font-semibold" data-testid="add-item-btn">Add</button>
            </div>
          </div>

          <textarea className="input-field w-full" rows="2" placeholder="Notes…" value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="input-notes" />

          <label className="flex items-center gap-2 py-2 cursor-pointer">
            <input type="checkbox" checked={!!form.is_shared} onChange={e => setForm({ ...form, is_shared: e.target.checked })} className="w-4 h-4 accent-[#F59E0B]" data-testid="toggle-share" />
            <span className="text-white text-sm flex items-center gap-1"><Sparkle size={14} weight="fill" className="text-[#39FF14]" /> Share with party/community</span>
          </label>

          <div className="flex gap-2 pt-3 sticky bottom-0 bg-[#0e0e16] pb-1">
            {!isNew && (
              <button onClick={remove} className="px-3 py-3 rounded-xl bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] text-sm" data-testid="delete-sheet-btn">
                <Trash size={16} />
              </button>
            )}
            <button onClick={save} disabled={saving} className="flex-1 py-3 rounded-xl bg-gradient-to-r from-[#F59E0B] to-[#EA580C] text-black font-semibold text-sm disabled:opacity-50" data-testid="save-sheet-btn">
              {saving ? 'Saving…' : (isNew ? 'Create Sheet' : 'Save Changes')}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function NumField({ label, value, onChange, min, max, testid }) {
  return (
    <div>
      <p className="text-[#71717A] text-[10px] uppercase tracking-wider mb-1">{label}</p>
      <input type="number" min={min} max={max} className="input-field w-full text-center" value={value}
        onChange={e => onChange(Number(e.target.value || 0))} data-testid={testid} />
    </div>
  );
}
